import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { CaretLeft, CaretRight, Eye, ChartBar } from "@phosphor-icons/react";

/**
 * VisitorsDayDialog — drill-down modal triggered by clicking the
 * "Visitors today" KPI tile. Shows hourly bars for any past day with
 * prev/next navigation and a date input for jumping to a specific day.
 *
 * Backend: GET /api/admin/hourly-uniques?date=YYYY-MM-DD returns
 * `{date, timezone, series:[{hour 0-23, unique_sessions, page_views}],
 *   total_unique_sessions, total_page_views}` — all timestamps bucketed
 * in America/New_York.
 */
export default function VisitorsDayDialog({ open, onOpenChange, initialDate }) {
  const today = useMemo(() => isoDateET(new Date()), []);
  const [date, setDate] = useState(initialDate || today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    setError("");
    api
      .get(`/admin/hourly-uniques?date=${encodeURIComponent(date)}`)
      .then(({ data }) => mounted && setData(data))
      .catch((e) => mounted && setError(e?.response?.data?.detail || "Couldn't load that day"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [open, date]);

  const series = data?.series || [];
  const peak = Math.max(1, ...series.map((s) => s.unique_sessions));
  const totalSessions = data?.total_unique_sessions ?? 0;
  const totalPageViews = data?.total_page_views ?? 0;
  const isToday = date === today;

  const shift = (deltaDays) => setDate(addDays(date, deltaDays));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="visitors-day-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <ChartBar weight="duotone" className="h-5 w-5 text-primary" />
            Visitors by hour
          </DialogTitle>
          <DialogDescription>
            Unique sessions per hour (Eastern Time). Owner &amp; admin self-traffic excluded.
          </DialogDescription>
        </DialogHeader>

        {/* Date controls */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="visitors-day-controls">
          <button
            type="button"
            onClick={() => shift(-1)}
            className="h-9 w-9 grid place-items-center rounded-full border border-border hover:bg-muted"
            aria-label="Previous day"
            data-testid="visitors-day-prev"
          >
            <CaretLeft weight="bold" className="h-4 w-4" />
          </button>
          <input
            type="date"
            value={date}
            max={today}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 px-3 rounded-full border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="visitors-day-date-input"
          />
          <button
            type="button"
            onClick={() => shift(1)}
            disabled={isToday}
            className="h-9 w-9 grid place-items-center rounded-full border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next day"
            data-testid="visitors-day-next"
          >
            <CaretRight weight="bold" className="h-4 w-4" />
          </button>
          {!isToday && (
            <button
              type="button"
              onClick={() => setDate(today)}
              className="h-9 px-3 rounded-full border border-border bg-card text-xs font-semibold hover:bg-muted"
              data-testid="visitors-day-today"
            >
              Jump to today
            </button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            {formatDateLong(date)}
          </span>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 gap-3" data-testid="visitors-day-totals">
          <KpiTile label="Unique sessions" value={totalSessions} loading={loading} />
          <KpiTile label="Page views" value={totalPageViews} loading={loading} />
        </div>

        {/* Hourly bar chart */}
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="visitors-day-chart">
          {error ? (
            <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
          ) : loading ? (
            <div className="h-44 grid place-items-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : totalSessions === 0 ? (
            <div className="h-44 grid place-items-center text-sm text-muted-foreground">
              No visitor activity recorded for this day.
            </div>
          ) : (
            <HourlyBars series={series} peak={peak} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KpiTile({ label, value, loading }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-display font-bold text-2xl leading-none">
        {loading ? "…" : value}
      </div>
    </div>
  );
}

function HourlyBars({ series, peak }) {
  return (
    <div className="space-y-1.5" data-testid="visitors-day-bars">
      {/* X-axis indicator labels (4-hour intervals) */}
      <div className="grid text-[9px] text-muted-foreground gap-px mb-1" style={{ gridTemplateColumns: "repeat(24, 1fr)" }}>
        {series.map((s) => (
          <div key={`label-${s.hour}`} className="text-center">
            {s.hour % 4 === 0 ? formatHourCompact(s.hour) : ""}
          </div>
        ))}
      </div>
      {/* Bars */}
      <div className="grid gap-px items-end h-32" style={{ gridTemplateColumns: "repeat(24, 1fr)" }}>
        {series.map((s) => {
          const h = peak > 0 ? Math.round((s.unique_sessions / peak) * 100) : 0;
          return (
            <div
              key={s.hour}
              className="relative group h-full flex items-end"
              data-testid={`visitors-day-bar-${s.hour}`}
            >
              <div
                className={
                  "w-full rounded-t-sm transition-colors " +
                  (s.unique_sessions > 0
                    ? "bg-primary group-hover:bg-primary/80"
                    : "bg-muted")
                }
                style={{ height: s.unique_sessions > 0 ? `${Math.max(h, 4)}%` : "2px" }}
              />
              {/* Hover tooltip */}
              {s.unique_sessions > 0 && (
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded-md bg-foreground text-background text-[10px] font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  {formatHourLong(s.hour)} · {s.unique_sessions}
                  {s.page_views > 0 && <> · {s.page_views} pv</>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        Peak {peak} {peak === 1 ? "session" : "sessions"} ·
        {' '}<Eye weight="duotone" className="inline h-3 w-3 -mt-px" /> hover bars for detail
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

function isoDateET(d) {
  // Format a Date as YYYY-MM-DD using America/New_York calendar day.
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(d);
}

function addDays(isoDate, n) {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

function formatDateLong(isoDate) {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}

function formatHourCompact(h) {
  if (h === 0) return "12a";
  if (h === 12) return "12p";
  return h > 12 ? `${h - 12}p` : `${h}a`;
}

function formatHourLong(h) {
  if (h === 0) return "12am";
  if (h === 12) return "12pm";
  return h > 12 ? `${h - 12}pm` : `${h}am`;
}
